import axios from 'axios';

// --- TIMER CONSTANTS --- //
export const FLIGHTS_REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes
export const WEATHER_REFRESH_INTERVAL = 60 * 60 * 1000; // 1 hour
export const RATES_REFRESH_INTERVAL = 60 * 60 * 1000;   // 1 hour

// Private Module Lifecycle Core State Map
let widgetConfigs = null;
let dataUpdateCallback = null;
let reconnectHandlerInstance = null;

const services = {
    flights: {
        interval: null,
        failed: false,
        data: {},
        timer: FLIGHTS_REFRESH_INTERVAL,
        hasConfig: () => widgetConfigs?.airports?.length > 0,
        fetch: () => fetchBulkFlights(widgetConfigs.airports),
        isFailed: (res) => Object.keys(res).length === 0
    },
    weather: {
        interval: null,
        failed: false,
        data: {},
        timer: WEATHER_REFRESH_INTERVAL,
        hasConfig: () => widgetConfigs?.weatherLocations?.length > 0,
        fetch: () => fetchBulkWeather(widgetConfigs.weatherLocations),
        isFailed: (res) => Object.keys(res).length === 0
    },
    rates: {
        interval: null,
        failed: false,
        data: {},
        timer: RATES_REFRESH_INTERVAL,
        hasConfig: () => !!widgetConfigs?.rates,
        fetch: () => fetchBulkRates(widgetConfigs.rates),
        isFailed: (res) => {
            const hasGeneral = widgetConfigs.rates.general?.length > 0;
            const needsAccor = widgetConfigs.rates.needsAccor;
            return (needsAccor && res.accor === null) || (hasGeneral && Object.keys(res.general || {}).length === 0);
        }
    }
};

export const mergeGroupTemplate = async (presentationData = {}, groupTemplateData = {}, mergePosition = 0) => {
    try {
        if (!groupTemplateData?.slides || !Array.isArray(groupTemplateData.slides)) {
            console.warn("Group template has no slides to merge");
            return presentationData;
        }
        
        let result = JSON.parse(JSON.stringify(presentationData));
        // Set g_lock = true for all group template slides and their elements
        const groupSlides = groupTemplateData.slides.map(slide => {
            const slideClone = { ...slide };
            
            // Set g_lock for the slide
            if (!slideClone.settings) slideClone.settings = {};
            slideClone.settings.g_lock = true;
            
            // Set g_lock for all elements in the slide
            if (slideClone.elements && Array.isArray(slideClone.elements)) {
                slideClone.elements = slideClone.elements.map(element => {
                    const elementClone = { ...element };
                    if (!elementClone.settings) elementClone.settings = {};
                    elementClone.settings.g_lock = true;
                    return elementClone;
                });
            }
            
            return slideClone;
        });
        
        const groupPosition = mergePosition;
        
        if (!result.slides || !result.slides.length) {
            console.log("Presentation has no slides, showing only group template slides");
            result.slides = [];
        }
        
        result.slides.splice(groupPosition, 0, ...groupSlides);
        
        console.log(`Merged ${groupSlides.length} group template slides at position ${groupPosition}`);
        return result;
        
    } catch (err) {
        console.error("Error in mergeGroupTemplate:", err);
        return presentationData;
    }
};

export const fetchBulkFlights = async (airports) => {
    if (!airports.length) return {};
    try {
        const response = await axios.post('/airport_flights/', { airports });
        return response.data;
    } catch (error) {
        console.error("Bulk flight fetch failed:", error);
        return {};
    }
};

export const fetchBulkWeather = async (locations) => {
    if (!locations.length) return {};
    try {
        const response = await axios.post('/city_weather/', { locations });
        return response.data;
    } catch (error) {
        console.error("Bulk weather fetch failed:", error);
        return {};
    }
};

export const fetchBulkRates = async (configs) => {
    const results = { accor: null, general: {} };

    try {
        const requests = [];
        // Fetch Accor (Simple GET)
        if (configs.needsAccor) {
            requests.push(
                axios.get('/get_exchange_rates_accor/').then(res => {
                    results.accor = res.data; 
                })
            );
        }

        // Fetch General (POST with array of currencies)
        if (configs.general.length > 0) {
            requests.push(
                axios.post('/get_exchange_rates_general/', { currencies: configs.general }).then(res => {
                    results.general = res.data;
                })
            );
        }
        await Promise.all(requests);
        return results;
    } catch (error) {
        console.error("Bulk rates fetch failed:", error);
        return results;
    }
};

// --- CONFIG EXTRACTION --- //
export const getPresentationDataConfigs = (presentationData) => {
    const airports = new Set();
    const locationsMap = new Map();
    const generalCurrencies = new Set();
    let needsAccor = false;

    if (!presentationData || !presentationData.slides) {
        return { airports: [], weatherLocations: [], rates: { general: [], needsAccor: false } };
    }

    presentationData.slides.forEach(slide => {
        slide.elements?.forEach(({ name, settings }) => {
            if (name === 'FlightsWidget' && settings?.airport?.value) {
                airports.add(settings.airport.value);
            } 
            else if (name === 'WeatherWidget') {
                const locationName = settings?.timezone?.name || "Porto, Portugal";
                const [city, country] = locationName.split(", ");
                if (city && country) {
                    locationsMap.set(`${city.trim()}/${country.trim()}`, { city: city.trim(), country: country.trim() });
                }
            } 
            else if (name === 'RatesWidget') {
                if (settings?.source === 'accor') {
                    needsAccor = true;
                } else if (settings?.currency) {
                    generalCurrencies.add(settings.currency);
                }
            }
        });
    });

    return {
        airports: Array.from(airports),
        weatherLocations: Array.from(locationsMap.values()),
        rates: {
            general: Array.from(generalCurrencies),
            needsAccor
        }
    };
};

// --- CORE UTILITY WORKER --- //
const executeServiceFetch = async (serviceKey) => {
    const service = services[serviceKey];
    if (!service.hasConfig()) return;

    const res = await service.fetch();
    if (service.isFailed(res)) {
        service.failed = true;
    } else {
        service.data = res;
        service.failed = false;
        
        if (dataUpdateCallback) {
            dataUpdateCallback({
                flights: services.flights.data,
                weather: services.weather.data,
                rates: services.rates.data
            });
        }
    }
};

// --- SUBSYSTEM ENGINE (INIT & DESTROY MANAGEMENT) --- //
export const initWidgetDataServices = async (presentation, onDataUpdate) => {
    destroyWidgetDataServices();

    dataUpdateCallback = onDataUpdate;
    widgetConfigs = getPresentationDataConfigs(presentation);

    reconnectHandlerInstance = () => {
        console.log("Internet reconnected. Dispatching only failed processes...");
        Object.keys(services).forEach(key => {
            if (services[key].failed) executeServiceFetch(key);
        });
    };
    window.addEventListener('online', reconnectHandlerInstance);

    // Initial Parallel Processing Batch
    await Promise.all(Object.keys(services).map(key => executeServiceFetch(key)));

    // Managed Interval Loops Setup
    Object.keys(services).forEach(key => {
        services[key].interval = setInterval(() => executeServiceFetch(key), services[key].timer);
    });
};

export const destroyWidgetDataServices = () => {
    Object.keys(services).forEach(key => {
        if (services[key].interval) clearInterval(services[key].interval);
        services[key].interval = null;
        services[key].failed = false;
        services[key].data = {};
    });

    if (reconnectHandlerInstance) {
        window.removeEventListener('online', reconnectHandlerInstance);
        reconnectHandlerInstance = null;
    }

    widgetConfigs = null;
    dataUpdateCallback = null;
};

export const isEPaperValue = (val) => {
    return String(val) === 'true';
};